import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, ShoppingCart, Menu } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NAV_LINKS, BRAND } from "@/data/content";
import { useCart } from "@/context/CartContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export const Navbar = () => {
  const { count } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* top strip */}
      <div className="hidden md:block bg-brand-navy text-white/90">
        <div className="container-x flex items-center justify-between h-10 text-sm">
          <p className="font-semibold tracking-wide">
            <span className="text-brand-red font-bold">●</span> 24/7 US-Based Monitoring &nbsp;·&nbsp; TMA Five Diamond Certified
          </p>
          <a
            href={BRAND.phoneHref}
            data-testid="topbar-phone"
            className="inline-flex items-center gap-2 font-semibold hover:text-white link-underline"
          >
            <Phone className="w-4 h-4 text-brand-red" strokeWidth={2.5} />
            {BRAND.phone}
          </a>
        </div>
      </div>

      {/* main bar */}
      <div
        className={`bg-white border-b transition-shadow duration-300 ${
          scrolled ? "shadow-[0_10px_30px_-18px_rgba(11,31,58,0.4)] border-brand-line" : "border-transparent"
        }`}
      >
        <div className="container-x flex items-center justify-between h-[74px]">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-semibold text-[15px] link-underline transition-colors ${
                    isActive || (l.to === "/shop" && location.pathname.startsWith("/products"))
                      ? "text-brand-red"
                      : "text-brand-navy hover:text-brand-red"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2.5">
            <Link
              to="/cart"
              data-testid="nav-cart"
              aria-label="View cart"
              className="btn-focus relative grid place-items-center w-12 h-12 rounded-xl border border-brand-line text-brand-navy hover:border-brand-navy hover:bg-brand-bg transition-colors"
            >
              <ShoppingCart className="w-5 h-5" strokeWidth={2.2} />
              {count > 0 && (
                <span
                  data-testid="cart-count"
                  className="absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1 grid place-items-center rounded-full bg-brand-red text-white text-xs font-bold"
                >
                  {count}
                </span>
              )}
            </Link>

            <a
              href={BRAND.phoneHref}
              data-testid="nav-call-button"
              className="btn-focus hidden sm:inline-flex items-center gap-2 h-12 px-5 rounded-full bg-brand-red text-white font-bold hover:bg-brand-red-600 transition-colors shadow-red"
            >
              <Phone className="w-4 h-4" strokeWidth={2.6} />
              <span className="hidden xl:inline">{BRAND.phone}</span>
              <span className="xl:hidden">Call</span>
            </a>

            {/* mobile menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-button"
                  aria-label="Open menu"
                  className="btn-focus lg:hidden grid place-items-center w-12 h-12 rounded-xl border border-brand-line text-brand-navy"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[86%] max-w-sm p-0">
                <div className="p-6 border-b border-brand-line">
                  <Logo />
                </div>
                <nav className="flex flex-col p-4">
                  {NAV_LINKS.concat([{ label: "Contact", to: "/contact" }]).map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `px-4 py-4 rounded-xl font-bold text-lg ${
                          isActive ? "bg-brand-bg text-brand-red" : "text-brand-navy"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  ))}
                </nav>
                <div className="p-6 mt-auto">
                  <a
                    href={BRAND.phoneHref}
                    data-testid="mobile-call-button"
                    className="btn-focus flex items-center justify-center gap-2 h-14 rounded-full bg-brand-red text-white font-bold text-lg"
                  >
                    <Phone className="w-5 h-5" strokeWidth={2.6} />
                    {BRAND.phone}
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
